import Button from "@src/components/Button/Button";
import { ButtonVariant } from "@src/components/Button/Button.constants";
import { DatePicker } from "@src/components/DatePicker/DatePicker";
import { Input } from "@src/components/Input/Input";
import { SelectYup } from "@src/components/Select/SelectYup";
import Switch from "@src/components/Switch/Switch";
import { yupResolver } from "@hookform/resolvers/yup";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useForm, useWatch } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import { CloseIcon } from "@src/assets/icons/CloseIcon";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import {
	resetResults,
	saveMarketplaceEvents,
	searchMarketplaceEvents,
	MarketplaceEvent,
	SearchMarketplaceEventsPayload,
} from "@src/store/pullEventsSlice";
import { Divider, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalWrapper } from "../Modal.styled";
import {
	ContentContainer,
	FieldGroup,
	FiltersRow,
	FiltersSection,
	SearchButtonWrapper,
	SectionTitle,
	TooltipWrapper,
	ResultsSection,
	ResultsTitle,
	ResultsTable,
	ResultsHeaderRow,
	ResultsRow,
	ResultsCell,
	EllipsisText,
	StatusBadge,
	ResultsMessage,
	SelectionInfo,
	SelectionWarning,
	CheckboxCell,
	StyledCheckbox,
	ResultsTableBody,
	ResultsTableWrapper,
} from "./PullEventsModal.styled";
import { PullEventsFormValues, pullEventsDefaultValues, PullEventsValidation } from "./PullEventsModal.constants";
import { toast } from "sonner";
import { FilterButton } from "@src/components/Header/Header.styled";
import { ResetIcon } from "@src/assets/icons/ResetIcon";
import { filterVisibleMarketplaceOptions } from "@src/constants/marketplaceVisibility";

interface PullEventsModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const marketplaceOptions = filterVisibleMarketplaceOptions([
	{ label: "Ticketmaster", value: "TICKET_MASTER" },
	{ label: "Viagogo", value: "VIA_GOGO" },
	{ label: "AXS", value: "AXS" },
	{ label: "TicketNetwork", value: "TICKET_NETWORK" },
	{ label: "GameTime", value: "GAME_TIME" },
	{ label: "SeatGeek", value: "SEAT_GEEK" },
]);

const END_DATE_TOOLTIP_ID = "pull-events-end-date-tooltip";
const RESULTS_TOOLTIP_ID = "pull-events-results-tooltip";
const EVENT_NAME_TOOLTIP_ID = "pull-events-event-name-tooltip";
const EVENT_ID_TOOLTIP_ID = "pull-events-event-id-tooltip";
const SEAT_GEEK_ACTIVE_STATUS = "normal";
const SEARCH_DISABLED_TOOLTIP_ID = "search-disabled-tooltip";
const SAVE_PENDING_AFTER_MS = 45_000;
const PENDING_MANUAL_PULL_SAVE_TTL_MS = 15 * 60 * 1000;
const PENDING_MANUAL_PULL_SAVE_STORAGE_KEY = "manualPullPendingSaves";
const FORCE_MANUAL_PULL_PENDING_SAVE_STORAGE_KEY = "forceManualPullPendingSave";
const FORCE_MANUAL_PULL_PENDING_SAVE_NO_REQUEST_STORAGE_KEY = "forceManualPullPendingSaveNoRequest";

const extractDateTimeParts = (value?: string | null) => {
	if (!value) {
		return { datePart: "", timePart: "" };
	}

	const trimmedValue = value.trim();
	if (!trimmedValue) {
		return { datePart: "", timePart: "" };
	}

	if (trimmedValue.includes("T")) {
		const [datePart, timePart = ""] = trimmedValue.split("T");
		return { datePart, timePart };
	}

	const [datePart = "", ...rest] = trimmedValue.split(/\s+/);
	return { datePart, timePart: rest.join(" ") };
};

const formatDateString = (datePart: string) => {
	const trimmed = datePart.trim();
	if (!trimmed) {
		return "-";
	}

	const normalized = trimmed.replace(/-/g, "/");
	const segments = normalized.split("/").filter(Boolean);

	if (segments.length === 3) {
		let [first, second, third] = segments;

		if (first.length === 4 && third.length <= 2) {
			// YYYY/MM/DD -> MM/DD/YYYY
			[first, second, third] = [second, third, first];
		}

		if (third.length === 4) {
			const month = first.padStart(2, "0");
			const day = second.padStart(2, "0");
			return `${month}/${day}/${third}`;
		}
	}

	return normalized;
};

const stripTimeDecorators = (timePart: string) => {
	let clean = timePart.trim();

	if (!clean) {
		return "";
	}

	const fractionalIndex = clean.indexOf(".");
	if (fractionalIndex !== -1) {
		clean = clean.slice(0, fractionalIndex);
	}

	// Remove timezone offsets like +02:00 or -0500
	const timezoneMatch = clean.match(/([+-][0-9]{2}:?[0-9]{2})$/);
	if (timezoneMatch?.index !== undefined) {
		clean = clean.slice(0, timezoneMatch.index).trim();
	}

	clean = clean.replace(/Z$/i, "").trim();

	// Remove trailing seconds if they exist (HH:mm:ss -> HH:mm)
	const secondsMatch = clean.match(/^([0-9]{1,2}:[0-9]{2})(?::[0-9]{2})$/);
	if (secondsMatch) {
		clean = secondsMatch[1];
	}

	return clean;
};

const formatTimeString = (timePart: string) => {
	const cleaned = stripTimeDecorators(timePart);

	if (!cleaned) {
		return "-";
	}

	const amPmMatch = cleaned.match(/\b(AM|PM)\b/i);
	if (amPmMatch) {
		const [timeComponent] = cleaned.split(/\s+/);
		return `${timeComponent} ${amPmMatch[0].toUpperCase()}`;
	}

	const [hoursStr, minutesStr] = cleaned.split(":");
	const parsedHour = Number.parseInt(hoursStr, 10);

	if (Number.isNaN(parsedHour) || !minutesStr) {
		return cleaned;
	}

	const period = parsedHour >= 12 ? "PM" : "AM";
	const normalizedHour = ((parsedHour + 11) % 12) + 1;
	const minutes = minutesStr.padStart(2, "0");

	return `${normalizedHour}:${minutes} ${period}`;
};

const getFormattedDateTime = (value?: string | null) => {
	const { datePart, timePart } = extractDateTimeParts(value);
	const date = datePart ? formatDateString(datePart) : "-";
	const time = timePart ? formatTimeString(timePart) : "-";

	return { date, time };
};

const isLikelyDisconnectedLongRunningSave = (message?: string | null) => {
	if (!message) {
		return false;
	}

	const normalizedMessage = message.toLowerCase();

	return [
		"499",
		"network error",
		"connection closed",
		"socket hang up",
		"timeout",
		"timed out",
	].some((marker) => normalizedMessage.includes(marker));
};

const getPendingManualPullSaveId = (event: MarketplaceEvent) => event.id ?? `${event.marketplace}-${event.marketplaceEventId}`;

const readPendingManualPullSaves = (): Record<string, number> => {
	if (typeof window === "undefined") {
		return {};
	}

	try {
		const raw = window.localStorage.getItem(PENDING_MANUAL_PULL_SAVE_STORAGE_KEY);
		const parsed = raw ? JSON.parse(raw) : {};
		const now = Date.now();
		const cleanedEntries = Object.entries(parsed ?? {}).filter((entry): entry is [string, number] => {
			const [eventId, expiresAt] = entry;
			return Boolean(eventId) && typeof expiresAt === "number" && expiresAt > now;
		});
		const cleaned = Object.fromEntries(cleanedEntries);

		if (cleanedEntries.length === 0) {
			window.localStorage.removeItem(PENDING_MANUAL_PULL_SAVE_STORAGE_KEY);
		} else if (raw !== JSON.stringify(cleaned)) {
			window.localStorage.setItem(PENDING_MANUAL_PULL_SAVE_STORAGE_KEY, JSON.stringify(cleaned));
		}

		return cleaned;
	} catch {
		window.localStorage.removeItem(PENDING_MANUAL_PULL_SAVE_STORAGE_KEY);
		return {};
	}
};

const markPendingManualPullSaves = (eventIds: Array<string>) => {
	if (typeof window === "undefined" || eventIds.length === 0) {
		return;
	}

	const currentEntries = readPendingManualPullSaves();
	const expiresAt = Date.now() + PENDING_MANUAL_PULL_SAVE_TTL_MS;

	for (const eventId of eventIds) {
		currentEntries[eventId] = expiresAt;
	}

	window.localStorage.setItem(PENDING_MANUAL_PULL_SAVE_STORAGE_KEY, JSON.stringify(currentEntries));
};

const shouldForceManualPullPendingSave = () => {
	if (typeof window === "undefined") {
		return false;
	}

	return window.localStorage.getItem(FORCE_MANUAL_PULL_PENDING_SAVE_STORAGE_KEY) === "true";
};

const shouldForceManualPullPendingSaveNoRequest = () => {
	if (typeof window === "undefined") {
		return false;
	}

	return window.localStorage.getItem(FORCE_MANUAL_PULL_PENDING_SAVE_NO_REQUEST_STORAGE_KEY) === "true";
};

const PullEventsModal: React.FC<PullEventsModalProps> = ({ isOpen, onClose }) => {
	const dispatch = useAppDispatch();
	const { dbCount, results, loading, error, saveStatus, saveError } = useAppSelector((state) => state.pullEvents);
	const { control, handleSubmit, setValue, reset, clearErrors } = useForm<PullEventsFormValues>({
		mode: "onSubmit",
		resolver: yupResolver(PullEventsValidation),
		defaultValues: pullEventsDefaultValues,
	});

	const eventIdValue = useWatch({
		control,
		name: "eventID",
	});

	const searchValue = useWatch({
		control,
		name: "search",
	});

	const startDateValue = useWatch({
		control,
		name: "startDate",
	});

	const endDateValue = useWatch({
		control,
		name: "endDate",
	});

	const marketplaceValue = useWatch({
		control,
		name: "marketplace",
	});

	const isViagogo = marketplaceValue === "VIA_GOGO";
	const isAXS = marketplaceValue === "AXS";
	const isSeatGeek = marketplaceValue === "SEAT_GEEK";
	const [isActive, setIsActive] = useState(true);
	const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
	const [selectionWarning, setSelectionWarning] = useState<string | null>(null);
	const [lastSearchCriteria, setLastSearchCriteria] = useState<SearchMarketplaceEventsPayload | null>(null);
	const [showPendingSaveState, setShowPendingSaveState] = useState(false);
	const [hasShownPendingSaveToast, setHasShownPendingSaveToast] = useState(false);
	const currentSaveEventIdsRef = useRef<Array<string>>([]);
	const isEventIdFilled = Boolean(eventIdValue?.trim());
	const isMarketplaceSelected = Boolean(marketplaceValue);

	const isOtherFiltersFilled =
		Boolean(searchValue?.trim()) ||
		Boolean(startDateValue) ||
		Boolean(endDateValue);

	const minEndDate = useMemo(() => {
		if (!startDateValue) {
			return undefined;
		}

		const parsedDate = new Date(startDateValue);
		return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
	}, [startDateValue]);

	useEffect(() => {
		if (isViagogo) {
			const nextEndDate = startDateValue ?? "";
			setValue("endDate", nextEndDate, { shouldValidate: Boolean(nextEndDate) });
			clearErrors("endDate");
		} else {
			clearErrors("endDate");
		}
	}, [isViagogo, setValue, startDateValue, clearErrors]);

	const formatDateForApi = (value?: string) => {
		if (!value) {
			return "";
		}

		const [month, day, year] = value.split("/");

		if (!month || !day || !year) {
			return value;
		}

		return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
	};

	const onSubmit = handleSubmit(({ search, marketplace, startDate, endDate, eventID }) => {
		const trimmedSearch = search.trim();
		const dateFrom = formatDateForApi(startDate);
		const dateTo = formatDateForApi(endDate || startDate);

		const payload: SearchMarketplaceEventsPayload = {
			marketplace,
			eventName: trimmedSearch,
			dateFrom,
			dateTo,
			eventID,
		};

		setLastSearchCriteria(payload);
		dispatch(searchMarketplaceEvents(payload));
	});

	const formattedResults = useMemo(
		() =>
			results.map((event) => {
				const rowId = event.id ?? `${event.marketplace}-${event.marketplaceEventId}`;
				const metadata = (event.metadata ?? {}) as {
					dateTime?: { value?: string };
					venue?: { name?: string, title?: string };
					status?: string;
					active?: boolean;
					venueName?: string;
					_embedded?: { venue?: { name?: string } };
				};
				const audit = (event.audit ?? {}) as { status?: string };

				const { date, time } = getFormattedDateTime(event.localEventDateTime ?? event.eventDateTime ?? null);
				const embeddedVenueName = event.marketplace === "VIA_GOGO" ? metadata._embedded?.venue?.name : undefined;
				const venueName = metadata.venue?.name ?? metadata.venue?.title ?? metadata.venueName ?? embeddedVenueName ?? "-";
				const resolvedStatus =
					audit.status ??
					metadata.status ??
					(metadata.active != null ? (metadata.active ? "Active" : "Inactive") : null) ??
					"N/A";
				const normalizedStatus = (resolvedStatus ?? "N/A").toString().trim().toLowerCase();
				const isSeatGeekActive = event.marketplace === "SEAT_GEEK" && normalizedStatus === SEAT_GEEK_ACTIVE_STATUS;
				const statusText = isSeatGeekActive ? "Active" : (resolvedStatus ?? "N/A").toString();
				const isActiveStatus =
					isSeatGeekActive || metadata.active === true || normalizedStatus === "active";

				return {
					rowId,
					id: event.id,
					marketplaceEventId: event.marketplaceEventId,
					name: event.name,
					date,
					time,
					venue: venueName || "-",
					status: statusText,
					isActiveStatus,
				};
			}),
		[results],
	);

	const filteredResults = useMemo(
		() => (isActive ? formattedResults.filter((result) => result.isActiveStatus) : formattedResults),
		[formattedResults, isActive],
	);

	const showResultsTable = filteredResults.length > 0;
	const isLoading = loading === "pending";
	const isSaving = saveStatus === "pending";
	const isSavePendingUi = showPendingSaveState;
	const shouldSuppressSaveError =
		hasShownPendingSaveToast && isLikelyDisconnectedLongRunningSave(saveError);
	const isSearchDisabledBecauseMarketplace = !isMarketplaceSelected && !isLoading;

	const selectedEvents = useMemo(() => {
		const eventsByRowId = new Map<string, MarketplaceEvent>();
		results.forEach((event) => {
			const rowId = event.id ?? `${event.marketplace}-${event.marketplaceEventId}`;
			eventsByRowId.set(rowId, event);
		});

		return selectedIds.map((id) => eventsByRowId.get(id)).filter((event): event is MarketplaceEvent => Boolean(event));
	}, [results, selectedIds]);
	const isSaveActionDisabled = selectedEvents.length === 0 || (isSaving && !isSavePendingUi);
	const selectionLimitReached = selectedIds.length >= 5;

	const triggerPendingSaveUi = useCallback(() => {
		if (hasShownPendingSaveToast || currentSaveEventIdsRef.current.length === 0) {
			return;
		}

		setShowPendingSaveState(true);
		setHasShownPendingSaveToast(true);
		markPendingManualPullSaves(currentSaveEventIdsRef.current);
		toast.info("Save request was accepted and is still being processed. It may take a few minutes to complete.", {
			duration: 10000,
		});
	}, [hasShownPendingSaveToast]);

	const handleToggleRow = (eventId: string) => {
		setSelectionWarning(null);
		setSelectedIds((prev) => {
			const isSelected = prev.includes(eventId);
			if (isSelected) {
				return prev.filter((id) => id !== eventId);
			}

			if (prev.length >= 5) {
				setSelectionWarning("You can select up to 5 events.");
				return prev;
			}

			return [...prev, eventId];
		});
	};

	const handleSave = async () => {
		if (selectedEvents.length === 0 || (isSaving && !isSavePendingUi)) {
			return;
		}

		if (!marketplaceValue) {
			toast.error("Marketplace is required before saving.");
			return;
		}

		const pendingSaveEntries = readPendingManualPullSaves();
		const pendingSelectedEvents = selectedEvents.filter((event) => pendingSaveEntries[getPendingManualPullSaveId(event)]);
		const eventsToSave = selectedEvents.filter((event) => !pendingSaveEntries[getPendingManualPullSaveId(event)]);

		if (eventsToSave.length === 0) {
			toast.info(
				selectedEvents.length === 1
					? "This event is still being processed. Please try again in a few minutes."
					: "These events are still being processed. Please try again in a few minutes.",
				{
					duration: 10000,
				},
			);
			return;
		}

		if (pendingSelectedEvents.length > 0) {
			toast.info(
				pendingSelectedEvents.length === 1
					? "One selected event is already being processed and was skipped."
					: `${pendingSelectedEvents.length} selected events are already being processed and were skipped.`,
				{
					duration: 10000,
				},
			);
		}

		setShowPendingSaveState(false);
		setHasShownPendingSaveToast(false);
		currentSaveEventIdsRef.current = eventsToSave.map(getPendingManualPullSaveId);

		if (shouldForceManualPullPendingSaveNoRequest()) {
			triggerPendingSaveUi();
			return;
		}

		await dispatch(
			saveMarketplaceEvents({
				marketplace: marketplaceValue,
				events: eventsToSave,
			}),
		);
	};

	const handleCancel = () => {
		setShowPendingSaveState(false);
		setHasShownPendingSaveToast(false);
		setSelectedIds([]);
		setSelectionWarning(null);
		dispatch(resetResults());
		onClose();
	};

	useEffect(() => {
		if (!isSaving || hasShownPendingSaveToast) {
			return;
		}

		if (shouldForceManualPullPendingSave()) {
			triggerPendingSaveUi();
			return;
		}

		const timeoutId = window.setTimeout(() => {
			triggerPendingSaveUi();
		}, SAVE_PENDING_AFTER_MS);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [hasShownPendingSaveToast, isSaving, triggerPendingSaveUi]);

	useEffect(() => {
		if (loading === "pending") {
			setSelectedIds([]);
			setSelectionWarning(null);
		}
	}, [loading]);

	useEffect(() => {
		const visibleIds = new Set(filteredResults.map((result) => result.rowId));
		setSelectedIds((prev) => {
			const filteredSelection = prev.filter((id) => visibleIds.has(id));
			return filteredSelection.length === prev.length ? prev : filteredSelection;
		});
		setSelectionWarning(null);
	}, [isActive, filteredResults]);

	useEffect(() => {
		if (!isOpen) {
			setShowPendingSaveState(false);
			setHasShownPendingSaveToast(false);
			setSelectedIds([]);
			setSelectionWarning(null);
			setIsActive(true);
			reset(pullEventsDefaultValues);
		}
	}, [isOpen, reset]);

	useEffect(() => {
		if (saveStatus === "succeeded") {
			toast.success("Marketplace events saved successfully");
			setShowPendingSaveState(false);
			setHasShownPendingSaveToast(false);
			currentSaveEventIdsRef.current = [];
			setSelectedIds([]);
			setSelectionWarning(null);

			reset(pullEventsDefaultValues);

			setIsActive(true);
			dispatch(resetResults());
			setLastSearchCriteria(null);

		} else if (saveStatus === "failed") {
			setShowPendingSaveState(false);

			if (!shouldSuppressSaveError) {
				toast.error(saveError ?? "Failed to save marketplace events");
			}

			currentSaveEventIdsRef.current = [];
		}
	}, [saveStatus, saveError, dispatch, reset, shouldSuppressSaveError]);

	useEffect(() => {
		if (isEventIdFilled) {
			clearErrors(["search", "startDate", "endDate"]);
		}
	}, [isEventIdFilled, clearErrors]);


	useEffect(() => {
		if (!marketplaceValue) {
			return;
		}

		reset({
			...pullEventsDefaultValues,
			marketplace: marketplaceValue,
		});

		setSelectedIds([]);
		setSelectionWarning(null);
		setIsActive(true);

		dispatch(resetResults());

		setLastSearchCriteria(null);
	}, [marketplaceValue, reset, dispatch]);

	if (!isOpen) {
		return null;
	}

	const portalTarget = document.querySelector("#modal");

	if (!portalTarget) {
		return null;
	}

	const handleReset = () => {
		setShowPendingSaveState(false);
		setHasShownPendingSaveToast(false);
		reset(pullEventsDefaultValues);
		setSelectedIds([]);
		setSelectionWarning(null);
		setIsActive(true);
		dispatch(resetResults());
	};

	return createPortal(
		<ModalWrapper>
			<ModalContent>
				<ModalHeader>
					Manual Event Pull
					<CloseIcon onClick={onClose} />
				</ModalHeader>
				<ModalBody>
					<ContentContainer>
						<SectionTitle>Event Details</SectionTitle>
						<FiltersSection onSubmit={onSubmit}>
							<FiltersRow style={{ justifyContent: "flex-start" }}>
								<FieldGroup $minWidth="225px" style={{ flex: "0 0 auto" }}>
									<label style={{ fontSize: "12px", marginBottom: 3 }}>Marketplace</label>
									<SelectYup
										control={control}
										name="marketplace"
										placeholder="Marketplace"
										options={marketplaceOptions}
									/>
								</FieldGroup>
							</FiltersRow>
							<FiltersRow>
								<FieldGroup>
									<label style={{ fontSize: "12px", marginBottom: 3 }}>MP Event ID</label>
									<TooltipWrapper
										data-tooltip-id={isSeatGeek ? EVENT_ID_TOOLTIP_ID : undefined}
										data-tooltip-content={
											isSeatGeek
												? "Paste the numeric ID only. On SeatGeek it is the last part of the event URL."
												: undefined
										}
									>
										<Input
											control={control}
											name="eventID"
											placeholder="Search for an event ID"
											disableErrorMessage
											disabled={isOtherFiltersFilled}
										/>
									</TooltipWrapper>
									{isSeatGeek && <Tooltip id={EVENT_ID_TOOLTIP_ID} place="right" style={{ zIndex: 9999 }} />}
								</FieldGroup>
								<FieldGroup>
									<label style={{ fontSize: "12px", marginBottom: 3 }}>Event Name</label>
									<TooltipWrapper
										data-tooltip-id={isAXS ? EVENT_NAME_TOOLTIP_ID : undefined}
										data-tooltip-content={isAXS ? "Event name is not available for AXS." : undefined}
									>
										<Input
											control={control}
											name="search"
											placeholder="Search for an event"
											disableXButton
											disableErrorMessage
											disabled={isAXS || isEventIdFilled}
										/>
									</TooltipWrapper>
									{isAXS && <Tooltip id={EVENT_NAME_TOOLTIP_ID} place="right" style={{ zIndex: 9999 }} />}
								</FieldGroup>

								<FieldGroup $minWidth="220px">
									<label style={{ fontSize: "12px", marginBottom: 3 }}>Start Date</label>
									<DatePicker
										control={control}
										name="startDate"
										placeholder="Start Date"
										wrapperClassName={"modal-pull-date-picker"}
										disabled={isEventIdFilled}
									/>
								</FieldGroup>
								<FieldGroup $minWidth="220px">
									<label style={{ fontSize: "12px", marginBottom: 3 }}>End Date</label>
									<TooltipWrapper
										data-tooltip-id={isViagogo ? END_DATE_TOOLTIP_ID : undefined}
										data-tooltip-content={isViagogo ? "For Viagogo there is no end date." : undefined}>
										<DatePicker
											control={control}
											name="endDate"
											placeholder="End Date"
											minDate={minEndDate}
											wrapperClassName={"modal-pull-date-picker"}
											disabled={isViagogo || isEventIdFilled}
										/>
									</TooltipWrapper>
									{isViagogo && <Tooltip id={END_DATE_TOOLTIP_ID} place="right" style={{ zIndex: 9999 }} />}
								</FieldGroup>
								<FieldGroup $minWidth="110px" $flex="0">
									<Switch label="Only Active" checked={isActive} onChange={setIsActive} />
								</FieldGroup>
								<div><Button type="button" onClick={handleReset} variant={ButtonVariant.Quaternary}>
									<FilterButton>
										<ResetIcon />
									</FilterButton>
								</Button>
								</div>
								<SearchButtonWrapper>
									<TooltipWrapper
										data-tooltip-id={
											isSearchDisabledBecauseMarketplace
												? SEARCH_DISABLED_TOOLTIP_ID
												: undefined
										}
										data-tooltip-content={
											isSearchDisabledBecauseMarketplace
												? "Marketplace is required to perform search."
												: undefined
										}
									>
										<div>
											<Button
												disabled={!isMarketplaceSelected || isLoading}
												type="submit"
												variant={ButtonVariant.Primary}
												style={{ width: "100%" }}
											>
												Search
											</Button>
										</div>
									</TooltipWrapper>

									{isSearchDisabledBecauseMarketplace && (
										<Tooltip
											id={SEARCH_DISABLED_TOOLTIP_ID}
											place="top"
											style={{ zIndex: 9999 }}
										/>
									)}
								</SearchButtonWrapper>
							</FiltersRow>
							<div style={{ fontSize: "12px", color: "#8C8C8C", marginTop: "3px"}}>
								Searching by MP Event ID – Event Name and Dates will be ignored.
							</div>
						</FiltersSection>
					</ContentContainer>
					<Divider />
					<ResultsSection>
						<SelectionInfo>
							Selected {selectedIds.length} of 5 allowed.
							{selectionWarning ? <SelectionWarning> {selectionWarning}</SelectionWarning> : null}
						</SelectionInfo>
						<ResultsTitle>Select the most accurate match</ResultsTitle>
						{isLoading && <ResultsMessage>Searching marketplace events...</ResultsMessage>}
						{error && !isLoading && <ResultsMessage>{error}</ResultsMessage>}
						{saveError && !isSaving && !isSavePendingUi && !shouldSuppressSaveError && <ResultsMessage>{saveError}</ResultsMessage>}
						{showResultsTable && !isLoading && !error && (
							<ResultsTableWrapper>
								<ResultsTable>
									<ResultsHeaderRow>
										<span />
										<span>MP Event ID</span>
										<span>Event Name</span>
										<span>Date</span>
										<span>Time</span>
										<span>Venue</span>
										<span>Status</span>
									</ResultsHeaderRow>
									<ResultsTableBody>
										{filteredResults.map((result, index) => (
											<ResultsRow
												key={result.rowId ?? `${result.name}-${index}`}
												$isOdd={index % 2 === 1}
												$isSelected={selectedIds.includes(result.rowId ?? "")}
											>
												<CheckboxCell>
													<StyledCheckbox
														checked={selectedIds.includes(result.rowId ?? "")}
														onChange={() => {
															if (result.rowId) {
																handleToggleRow(result.rowId);
															}
														}}
														disabled={selectionLimitReached && !selectedIds.includes(result.rowId ?? "")}
														aria-label={`Select ${result.name}`}
													/>
												</CheckboxCell>
												<ResultsCell
													data-tooltip-id={RESULTS_TOOLTIP_ID}
													data-tooltip-content={result.marketplaceEventId || "-"}
													$align="left"
												>
													<EllipsisText>{result.marketplaceEventId || "-"}</EllipsisText>
												</ResultsCell>
												<ResultsCell
													data-tooltip-id={RESULTS_TOOLTIP_ID}
													data-tooltip-content={result.name || "-"}
													$align="left"
												>
													<EllipsisText>{result.name || "-"}</EllipsisText>
												</ResultsCell>
												<ResultsCell
													data-tooltip-id={RESULTS_TOOLTIP_ID}
													data-tooltip-content={result.date}
												>
													{result.date}
												</ResultsCell>
												<ResultsCell
													data-tooltip-id={RESULTS_TOOLTIP_ID}
													data-tooltip-content={result.time}
												>
													{result.time}
												</ResultsCell>
												<ResultsCell
													data-tooltip-id={RESULTS_TOOLTIP_ID}
													data-tooltip-content={result.venue}
												>
													{result.venue}
												</ResultsCell>
												<ResultsCell
													data-tooltip-id={RESULTS_TOOLTIP_ID}
													data-tooltip-content={result.status}
												>
													<StatusBadge>{result.status}</StatusBadge>
												</ResultsCell>
											</ResultsRow>
										))}
									</ResultsTableBody>
								</ResultsTable>
							</ResultsTableWrapper>
						)}
						{!isLoading && !error && !showResultsTable && (
							<ResultsMessage>
								{dbCount > 0
									? dbCount === 1
										? "No new events were found. For the filters entered, 1 event already exists in the EMT catalog. Please check whether the event is already mapped. If it is not mapped, you can proceed to map it from the Unmapped page."
										: "No new events were found. For the filters entered, multiple events already exist in the EMT catalog. Please check whether they are already mapped. If any of them are not mapped, you can proceed to map them from the Unmapped page."
									: "No events found for the selected filters."}
							</ResultsMessage>
						)}
						<Tooltip id={RESULTS_TOOLTIP_ID} place="top" style={{ zIndex: 9999 }} />
					</ResultsSection>
				</ModalBody>
				<ModalFooter>
					<Button variant={ButtonVariant.Tertiary} onClick={handleCancel} disabled={isSaving && !isSavePendingUi}>
						Cancel
					</Button>
					<Button
						variant={ButtonVariant.Primary}
						onClick={handleSave}
						disabled={isSaveActionDisabled}
					>
						{isSaving && !isSavePendingUi ? "Saving..." : "Save"}
					</Button>
				</ModalFooter>
			</ModalContent>
		</ModalWrapper>,
		portalTarget,
	);
};

export default PullEventsModal;
